/**
=========================================================
* Public Player Registration - v1.0.0
=========================================================
* 
* Modern, responsive public registration form for cricket series
* Features: Local storage, quick fill, dynamic form fields
*/

import { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";

// @mui material components
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Fade,
  Chip,
  Divider,
} from "@mui/material";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";

// Custom components
import RegistrationForm from "./components/RegistrationForm";
import SeriesHeader from "./components/SeriesHeader";
import ApplicationStatusCheck from "./components/ApplicationStatusCheck";

// API service
import { publicRegistrationAPI } from "services/api";

// Icons
import SportsCricketIcon from "@mui/icons-material/SportsCricket";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

function PublicRegistration() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // Get registration code from URL params with fallback
  const publicRegistrationCode = searchParams.get("c") || 
    new URLSearchParams(location.search).get("c");

  const [seriesData, setSeriesData] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [refInfo, setRefInfo] = useState({ ref_name: null, ref_mobile_no: null, req_series_participant_id: null });

  // Load series details on component mount
  useEffect(() => {
    // Small delay to ensure URL is fully loaded
    const timer = setTimeout(() => {
      if (publicRegistrationCode && publicRegistrationCode.trim()) {
        loadSeriesDetails();
      } else {
        setError("Public registration code is required.");
        setLoading(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [publicRegistrationCode, searchParams, location.search]);

  const loadSeriesDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await publicRegistrationAPI.getSeriesDetails({
        public_registration_code: publicRegistrationCode,
      });

      if (response.data.status === 1) {
        setSeriesData(response.data.series_data);
        setSuggestions(response.data.suggestions);
      } else {
        setError(response.data.message || "Failed to load series details");
      }
    } catch (err) {
      console.error("Error loading series details:", err);
      setError("Failed to load series details. Please check your registration code.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationSuccess = (refData = {}) => {
    setRefInfo({
      ref_name: refData.ref_name || null,
      ref_mobile_no: refData.ref_mobile_no || null,
      req_series_participant_id: refData.req_series_participant_id || null
    });
    setRegistrationSuccess(true);
  };

  if (loading) {
    return (
      <SoftBox
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgColor="grey-100"
      >
        <Box textAlign="center">
          <CircularProgress size={60} color="primary" />
          <SoftTypography variant="h6" color="text" mt={2}>
            Loading registration details...
          </SoftTypography>
        </Box>
      </SoftBox>
    );
  }

  if (error) {
    return (
      <SoftBox
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgColor="grey-100"
        px={2}
      >
        <Container maxWidth="sm">
          <Card elevation={3}>
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <SoftButton
                variant="contained"
                color="primary"
                onClick={() => window.location.reload()}
                startIcon={<SportsCricketIcon />}
              >
                Try Again
              </SoftButton>
            </CardContent>
          </Card>
        </Container>
      </SoftBox>
    );
  }

  if (registrationSuccess) {
    return (
      <SoftBox
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgColor="grey-100"
        px={2}
      >
        <Container maxWidth="sm">
          <Fade in={true}>
            <Card elevation={3}>
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <PersonAddIcon sx={{ fontSize: 40, color: "white" }} />
                </Box>
                
                
                {/* Reference Information */}
                {(refInfo.ref_name || refInfo.ref_mobile_no || refInfo.req_series_participant_id) && (
                  <>
                  <SoftTypography variant="h4" color="success" fontWeight="bold" mb={2}>
                  Registration Successful!
                </SoftTypography>
                <SoftTypography variant="body1" color="text" mb={3}>
                  Thank you for registering for{" "}
                  <strong>{seriesData?.series_name}</strong>. We'll contact you soon with further details.
                </SoftTypography>
                  <Box
                    sx={{
                      mt: 3,
                      mb: 3,
                      p: 2,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "grey.300",
                    }}
                  >
                    <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={1.5}>
                      Reference Details (റഫറൻസ് വിവരങ്ങൾ)
                    </SoftTypography>
                    <Divider sx={{ mb: 2 }} />
                    {refInfo.ref_name && (
                      <Box mb={1.5}>
                        <SoftTypography variant="body2" color="text" fontWeight="medium" mb={0.5}>
                          Reference Name (റഫറൻസ് പേര്):
                        </SoftTypography>
                        <SoftTypography variant="body1" color="dark" fontWeight="bold">
                          {refInfo.ref_name}
                        </SoftTypography>
                      </Box>
                    )}
                    {refInfo.ref_mobile_no && (
                      <Box mb={1.5}>
                        <SoftTypography variant="body2" color="text" fontWeight="medium" mb={0.5}>
                          Reference Mobile Number (റഫറൻസ് മൊബൈൽ നമ്പർ):
                        </SoftTypography>
                        <SoftTypography variant="body1" color="dark" fontWeight="bold">
                          {refInfo.ref_mobile_no}
                        </SoftTypography>
                      </Box>
                    )}
                    {refInfo.req_series_participant_id && (
                      <Box>
                        <SoftTypography variant="body2" color="text" fontWeight="medium" mb={0.5}>
                          Reference ID (സീരീസ് പാർട്ടിസിപന്റ് ഐഡി):
                        </SoftTypography>
                        <SoftTypography variant="body1" color="dark" fontWeight="bold">
                          {refInfo.req_series_participant_id}
                        </SoftTypography>
                      </Box>
                    )}
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <SoftTypography variant="caption" color="text">
                        Please note down these reference details for your records. You can also take a screenshot of this page.
                      </SoftTypography>
                    </Alert>
                  </Box>
                  </>
                )}
                
                <SoftButton
                  variant="contained"
                  color="primary"
                  onClick={() => window.location.reload()}
                >
                  Register Another Player
                </SoftButton>
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </SoftBox>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seriesData?.series_name || "Cricket Registration"} - Player Registration</title>
        <meta name="description" content={`Register for ${seriesData?.series_name || "our cricket series"}. Join the tournament and showcase your cricket skills!`} />
        
        {/* Open Graph / Facebook / WhatsApp */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${seriesData?.series_name || "Cricket Registration"} - Player Registration`} />
        <meta property="og:description" content={`Register for ${seriesData?.series_name || "our cricket series"}. Join the tournament and showcase your cricket skills!`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="4CR - Cricket Registration" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${seriesData?.series_name || "Cricket Registration"} - Player Registration`} />
        <meta name="twitter:description" content={`Register for ${seriesData?.series_name || "our cricket series"}. Join the tournament and showcase your cricket skills!`} />
      </Helmet>

      <SoftBox
        minHeight="100vh"
        bgColor="grey-100"
        py={4}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {/* Series Header */}
            <Grid item xs={12}>
              <SeriesHeader seriesData={seriesData} />
            </Grid>

          {/* Registration Form */}
          <Grid item xs={12} md={8} lg={9}>
            <RegistrationForm
              seriesData={seriesData}
              suggestions={suggestions}
              publicRegistrationCode={publicRegistrationCode}
              onSuccess={handleRegistrationSuccess}
            />
          </Grid>

          {/* Sidebar Info */}
          <Grid item xs={12} md={4} lg={3}>
            <Card elevation={2}>
              <CardContent sx={{ p: 3 }}>
                <SoftTypography variant="h6" color="dark" fontWeight="bold" mb={2}>
                  Registration Info
                </SoftTypography>
                
                <Box mb={2}>
                  <SoftTypography variant="body2" color="text" fontWeight="medium">
                    Total Participants
                  </SoftTypography>
                  <Chip 
                    label={seriesData?.total_participants || "N/A"} 
                    color="primary" 
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>

                

                <Divider sx={{ my: 2 }} />

                <SoftTypography variant="body2" color="text" fontSize="0.875rem">
                  <strong>Note:</strong> Your registration will be confirmed once payment is verified.
                  Please keep your payment screenshot ready if required.
                </SoftTypography>
              </CardContent>
            </Card>
          </Grid>

          {/* Application Status Check */}
          {(seriesData?.show_check_application_status === "1" || seriesData?.show_check_application_status === 1) && (
            <Grid item xs={12}>
              <ApplicationStatusCheck
                publicRegistrationCode={publicRegistrationCode}
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </SoftBox>
    </>
  );
}

export default PublicRegistration;
